import React, { useEffect, useState } from 'react';
import { Carousel } from 'antd';
import { fetchBanners, Banner } from '../services/productService';
import '../styles/BannerSlider.css';

interface BannerSliderProps {
  pageKey: string;
  fallback?: React.ReactNode;
  autoplaySpeed?: number;
}

const BannerSlider: React.FC<BannerSliderProps> = ({ pageKey, fallback = null, autoplaySpeed = 4000 }) => {
  const [banners, setBanners] = useState<Banner[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetchBanners(pageKey)
      .then(setBanners)
      .finally(() => setLoaded(true));
  }, [pageKey]);

  if (!loaded) return null;
  if (banners.length === 0) return <>{fallback}</>;

  return (
    <div className="banner-slider">
      <Carousel autoplay autoplaySpeed={autoplaySpeed} dots pauseOnHover effect="fade">
        {banners.map((banner, i) => (
          <div key={banner.id} className="banner-slide-wrap">
            {banner.link_url ? (
              <a href={banner.link_url} target="_blank" rel="noreferrer" className="banner-slide-link">
                <BannerMedia banner={banner} eager={i === 0} />
              </a>
            ) : (
              <BannerMedia banner={banner} eager={i === 0} />
            )}
          </div>
        ))}
      </Carousel>
    </div>
  );
};

const BannerMedia: React.FC<{ banner: Banner; eager?: boolean }> = ({ banner, eager }) => {
  if (banner.media_type === 'video') {
    return (
      <video
        className="banner-media"
        src={banner.media_url}
        autoPlay
        muted
        loop
        playsInline
      />
    );
  }
  // The first slide is the LCP image — load it eagerly with high priority; the rest
  // of the carousel (off-screen) stays lazy so it doesn't compete for bandwidth.
  return (
    <img
      className="banner-media"
      src={banner.media_url}
      alt={banner.title || ''}
      loading={eager ? 'eager' : 'lazy'}
      decoding="async"
      {...{ fetchpriority: eager ? 'high' : 'low' }}
    />
  );
};

export default BannerSlider;
